import { useState } from "react";

import { Column, Row, Text } from "@/components";
import { LoadingIndicator } from "@/components/LoadingIndicator";
import { Button } from "@ui/button";
import { Input } from "@ui/input";

import { useUpdateSetPay } from "@/hooks/updateSets/useUpdateSetPay";

// Amounts read in the unit the candidate stated them in: values at or below
// $1,000 render as hourly, larger ones as annual (see formatting.payWithUnit).
const PAY_UNIT_HINT = "e.g. 25 (hourly) or 60000 (annual)";
// Neither field has been established for every candidate. Unset stays unset
// unless the staffer types something.
const UNKNOWN_PLACEHOLDER = "—";

export const PayEditor = ({
  currentPay,
  desiredPay,
  onDone,
  stafferId,
  userId,
}: {
  currentPay: number | null;
  desiredPay: number | null;
  onDone: () => void;
  stafferId: number;
  userId: number;
}) => {
  /* Database CRUD Operations */
  // Update
  const { isPending, mutate: savePay } = useUpdateSetPay();

  /* React Hooks */
  const [currentPayDraft, setCurrentPayDraft] = useState(
    currentPay != null ? String(currentPay) : "",
  );
  const [desiredPayDraft, setDesiredPayDraft] = useState(
    desiredPay != null ? String(desiredPay) : "",
  );

  /* Helpers */
  // A blank or unparseable draft means "leave the field as it is" — clearing a
  // pay through this editor is not supported, only correcting it.
  const parseDraft = (draft: string) => {
    const trimmed = draft.trim();
    if (!trimmed) return undefined;
    const value = Number(trimmed.replace(/[$,]/g, ""));
    if (!Number.isFinite(value) || value <= 0) return undefined;
    return value;
  };

  /* Handlers */
  const handleDone = () => {
    const draftCurrentPay = parseDraft(currentPayDraft);
    const draftDesiredPay = parseDraft(desiredPayDraft);
    // Only fields the staffer actually changed are sent, so an untouched field
    // does not get an EDIT row asserting the value it already held.
    const data = {
      ...(draftCurrentPay != null &&
        draftCurrentPay !== currentPay && { currentPay: draftCurrentPay }),
      ...(draftDesiredPay != null &&
        draftDesiredPay !== desiredPay && { desiredPay: draftDesiredPay }),
    };

    if (!Object.keys(data).length) {
      onDone();
      return;
    }

    savePay({ data, stafferId, userId }, { onSuccess: onDone });
  };

  /* Render */
  const renderPayInput = (
    label: string,
    draft: string,
    setDraft: (value: string) => void,
  ) => {
    return (
      <Column className="gap-1">
        <Text className="font-plex-mono text-[9px] font-medium uppercase tracking-wider text-slate-400">
          {label}
        </Text>
        <Input
          className="h-7 w-28 text-sm"
          disabled={isPending}
          inputMode="decimal"
          onChange={(event) => {
            setDraft(event.target.value);
          }}
          placeholder={UNKNOWN_PLACEHOLDER}
          title={PAY_UNIT_HINT}
          value={draft}
        />
      </Column>
    );
  };

  const render = () => {
    return (
      <Row className="flex-wrap items-end gap-3">
        {renderPayInput("Current pay", currentPayDraft, setCurrentPayDraft)}
        {renderPayInput("Desired pay", desiredPayDraft, setDesiredPayDraft)}
        <Button
          className="h-7 px-3 text-xs"
          disabled={isPending}
          onClick={handleDone}
          size="sm"
          type="button"
        >
          {isPending ? (
            <Row className="items-center gap-1.5">
              <LoadingIndicator gearColor="white" variant="small" />
              Saving
            </Row>
          ) : (
            "Done"
          )}
        </Button>
      </Row>
    );
  };

  return render();
};
